import {
  ProviderProps,
  ChangeEvent,
  ReactElement,
  createContext,
  useContext,
  useCallback,
} from 'react';

import { ItemsSearchFilters } from '~hooks/useItemsSearchFilters';

import {
  OrdersFiltersMenuV2FiltersItem,
  OrdersFiltersMenuV2AutocompleteRefs,
  OrdersFiltersMenuV2ContextValue,
  OrdersFiltersMenuV2ProviderProps,
} from './types';

const DEFAULT_FILTER_AUTOCOMPLETE_REFS: OrdersFiltersMenuV2AutocompleteRefs = {
  vehicles: { current: null },
  drivers: { current: null },
  services: { current: null },
  logisticsZones: { current: null },
  assignedUser: { current: null },
  sourceImports: { current: null },
  giverCompany: { current: null },
  itemState: { current: null },
  pickingState: { current: null },
  collectState: { current: null },
};

const DEFAULT_FILTER_STATE: ItemsSearchFilters = {
  // COMMONS
  search: '',
  expirationDateTimeFrom: null,
  expirationDateTimeTo: null,
  includeWithoutExpirationDate: null,
  dateFrom: null,
  dateTo: null,
  // SPECIFIC ITEMS
  finalizedDateTimeFrom: null,
  finalizedDateTimeTo: null,
  unit1Gt: '',
  unit1Lt: '',
  unit2Gt: '',
  unit2Lt: '',
  priceGt: '',
  priceLt: '',
  vehicles: null,
  drivers: null,
  services: null,
  zones: null,
  assignedUser: null,
  itemState: null,
  pickingState: null,
  sourceImports: null,
  // SPECIFIC COLLECT
  giverCompany: null,
  collectState: null,
  giverCollectState: null,
};

const DEFAULT_FILTER_ITEM: OrdersFiltersMenuV2FiltersItem = {
  autocompletesRefs: DEFAULT_FILTER_AUTOCOMPLETE_REFS,
  filters: DEFAULT_FILTER_STATE,
  activeFilters: DEFAULT_FILTER_STATE,
  onChangeFilterInput: () => {},
  onResetFilters: () => {},
  onApplyFilters: () => {},
  onClearFilters: () => {},
};

const OrdersFiltersMenuV2Context =
  createContext<OrdersFiltersMenuV2ContextValue>({
    type: 'inWarehouse',
    filters: {
      collect: DEFAULT_FILTER_ITEM,
      inWarehouse: DEFAULT_FILTER_ITEM,
      inTransit: DEFAULT_FILTER_ITEM,
      finalized: DEFAULT_FILTER_ITEM,
    },
    selectsData: {
      vehicles: { loading: true, list: [] },
      drivers: { loading: true, list: [] },
      services: { loading: true, list: [] },
      logisticsZones: { loading: true, list: [] },
      assignedUser: { loading: true, list: [] },
      giverCompany: { loading: true, list: [] },
    },
    handleChangeInputFilter: () => () => {},
    handleChangeInputDateFilter: () => () => () => {},
  });

export const useOrdersFiltersMenuV2Context =
  (): OrdersFiltersMenuV2ContextValue => useContext(OrdersFiltersMenuV2Context);

const OrdersFiltersMenuV2Provider = ({
  value,
  children,
}: ProviderProps<OrdersFiltersMenuV2ProviderProps>): ReactElement => {
  const handleChangeInputFilter = useCallback(
    (currentFilter: OrdersFiltersMenuV2FiltersItem) =>
      (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value: inputValue } = event.target ?? event.currentTarget;

        currentFilter.onChangeFilterInput(name, inputValue);
      },
    [],
  );

  const handleChangeInputDateFilter = useCallback(
    (currentFilter: OrdersFiltersMenuV2FiltersItem) =>
      (fieldName: string) =>
      (inputValue: Date | null) => {
        currentFilter.onChangeFilterInput(fieldName, inputValue);
      },
    [],
  );

  return (
    <OrdersFiltersMenuV2Context.Provider
      value={{
        ...value,
        handleChangeInputFilter,
        handleChangeInputDateFilter,
      }}
    >
      {children}
    </OrdersFiltersMenuV2Context.Provider>
  );
};

export default OrdersFiltersMenuV2Provider;
